import React, { useEffect, Suspense } from "react";
import { Router, Route, Switch, Redirect, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./App.css";
import moment from "moment";
import "moment/locale/es";
import DefaultRoute from "./components/RouteComponent/DefaultRoute";
import PublicRoutes from "./components/RouteComponent/PublicRoutes";
import PrivateRoute from "./components/RouteComponent/PrivateRoute";
import RedirectRoute from "./components/RouteComponent/RedirectRoute";
import LocationWrapper from "./components/LocationWrapper/LocationWrapper";
import Layout from "./components/Layout/Layout";
import CustomLoader from "./components/CustomLoader/CustomLoader";
import Header from "./components/Header/Header";
import Footer from "./components/Footer/Footer";
import useAuthorizeNetScript from "./CustomHooks/useAuthorizeNetScript.js";
import { DentalInsuranceProvider } from "./Contexts/DentalInsuranceProvider.jsx";

import {
  ROOT,
  SIGNUP,
  TERMS_AND_CONDITION_LANG,
  PRIVACY_POLICY_LANG,
  LOGIN,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
  DASHBOARD,
  CLINIC_DETAILS,
  PROVIDER_DETAILS,
  DEFAULT,
  BOOK_NEW_APPOINTMENT,
  ALL_APPOINTMENTS,
  APPOINTMENT_DETAILS,
  SPLASH,
  FAVORITES,
  SEARCH_LISTING,
  ACCOUNT_SECTION,
  LIST_PRACTICE,
  SUBMIT_FEEDBACK,
  USER_AGREEMENT,
  MANGO_VOICE,
} from "./Auth/Routes";
import { languageArray, languageKey } from "./components/LanguageSelector/LanguageSelectorConstant";
import Toaster from "./components/Toaster/Toaster.jsx";
import { getAuthToken } from "./Auth/getToken.js";
import { isSuccessfullyLoggedIn } from "./Store/Login/loginActions.js";
import i18n from "./i18";

const DefaultPage = React.lazy(
  () => import("./containers/DefaultPage/DefaultPage"),
);
const TermsAndConditions = React.lazy(
  () => import("./containers/TermsAndConditions/TermsAndConditions"),
);
const Login = React.lazy(() => import("./containers/Login/Login"));
const SignUp = React.lazy(() => import("./containers/SignUp/SignUp"));
const PrivacyPolicy = React.lazy(
  () => import("./containers/TermsAndConditions/PrivacyPolicy/PrivacyPolicy"),
);
const ForgotPassword = React.lazy(
  () => import("./containers/ForgotPassword/ForgotPassword"),
);
const ResetPassword = React.lazy(
  () => import("./containers/ResetPassword/ResetPassword"),
);
const ListYourPractice = React.lazy(
  () => import("./containers/ListYourPractice/ListYourPractice"),
);
const DashBoard = React.lazy(() => import("./containers/DashBoard/DashBoard"));
const ClinicDetails = React.lazy(
  () => import("./containers/ClinicDetails/ClinicDetails"),
);
const BookingFlow = React.lazy(
  () => import("./containers/BookingFlow/BookingFlow"),
);
const ProviderDetails = React.lazy(
  () => import("./containers/ProviderDetails/ProviderDetails"),
);
const Favorites = React.lazy(() => import("./containers/Favorites/Favorites"));
const SearchList = React.lazy(
  () => import("./containers/SearchList/SearchList"),
);
const AllAppointments = React.lazy(
  () => import("./containers/AllAppointments/AllAppointments"),
);
const AppointmentDetails = React.lazy(
  () => import("./containers/AppointmentDetails/AppointmentDetails"),
);
const AccountSection = React.lazy(
  () => import("./containers/AccountSection/AccountSection"),
);
const SubmitFeedback = React.lazy(
  () => import("./containers/SubmitFeedback/SubmitFeedback"),
);
const UserAgreement = React.lazy(
  () => import("./containers/TermsAndConditions/UserAgreement/UserAgreement"),
);
const MangoVoice = React.lazy(
  () => import("./components/MangoVoice/MangoVoice"),
);

const LangSetter = ({ children }) => {
  const { lang } = useParams();

  useEffect(() => {
    if (lang === languageKey.en || lang === languageKey.sp) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  return children;
};

const App = ({ history }) => {
  const dispatch = useDispatch();

  const isUserLoggedInFirstTime = useSelector(
    (state) => state.login.isLoginApiSuccess,
  );
  const isLoading = useSelector((state) => {
    return state.userProfile.showUserProfileLoader;
  });

  const { language, version: languageVersion } = useSelector(
    (state) => state.languageChange,
  );
  const languageObj = languageArray.find((item) => {
    return item.key === language;
  });

  moment.locale(languageObj?.locale);

  useEffect(() => {
    const token = getAuthToken();
    if (token && !isUserLoggedInFirstTime) {
      dispatch(isSuccessfullyLoggedIn(true));
    }
  }, []);

  useAuthorizeNetScript();

  return (
    <DentalInsuranceProvider>
      <Toaster />
      <Router history={history}>
        <Suspense fallback={<CustomLoader />}>
          <React.Fragment key={languageVersion}>
          {isLoading ? null : <Header />}
          <LocationWrapper>
            <Switch>
              <Route path={ROOT} exact>
                <DefaultRoute />
              </Route>
              <Route path={SPLASH} exact>
                <PublicRoutes component={<DefaultPage />} />
              </Route>
              <Route path={TERMS_AND_CONDITION_LANG} exact>
                <LangSetter>
                  <TermsAndConditions />
                </LangSetter>
              </Route>
              <Route path={PRIVACY_POLICY_LANG} exact>
                <LangSetter>
                  <PrivacyPolicy />
                </LangSetter>
              </Route>
              <Route path={USER_AGREEMENT} component={UserAgreement} exact />
              <Route path={SIGNUP} exact>
                <PublicRoutes component={<SignUp />} />
              </Route>
              <Route path={LOGIN} exact>
                <PublicRoutes component={<Login />} />
              </Route>
              <Route path={FORGOT_PASSWORD} exact>
                <PublicRoutes component={<ForgotPassword />} />
              </Route>
              <Route path={RESET_PASSWORD} exact>
                <PublicRoutes component={<ResetPassword />} />
              </Route>
              <Route path={LIST_PRACTICE} exact>
                <PublicRoutes component={<ListYourPractice />} />
              </Route>
              <Route path={MANGO_VOICE} exact>
                <PublicRoutes component={<MangoVoice />} />
              </Route>
              {isUserLoggedInFirstTime && (
                <Layout>
                  <Switch>
                    <Route path={DASHBOARD}>
                      <PrivateRoute component={<SearchList />} />
                    </Route>
                    <Route path={FAVORITES}>
                      <PrivateRoute component={<Favorites />} />
                    </Route>
                    <Route path={CLINIC_DETAILS}>
                      <PrivateRoute component={<ClinicDetails />} />
                    </Route>
                    <Route path={SEARCH_LISTING}>
                      <PrivateRoute component={<SearchList />} />
                    </Route>
                    <Route path={BOOK_NEW_APPOINTMENT}>
                      <PrivateRoute component={<BookingFlow />} />
                    </Route>
                    <Route path={PROVIDER_DETAILS}>
                      <PrivateRoute component={<ProviderDetails />} />
                    </Route>
                    <Route path={ALL_APPOINTMENTS}>
                      <PrivateRoute component={<AllAppointments />} />
                    </Route>
                    <Route path={APPOINTMENT_DETAILS}>
                      <PrivateRoute component={<AppointmentDetails />} />
                    </Route>
                    <Route path={ACCOUNT_SECTION}>
                      <PrivateRoute component={<AccountSection />} />
                    </Route>
                    <Route path={SUBMIT_FEEDBACK}>
                      <PrivateRoute component={<SubmitFeedback />} />
                    </Route>
                    <Route path={DEFAULT}>
                      <Redirect to={DASHBOARD} />
                    </Route>
                  </Switch>
                </Layout>
              )}
              <Route path={PROVIDER_DETAILS} exact>
                <PublicRoutes component={<ProviderDetails />} />
              </Route>
              <Route path={CLINIC_DETAILS} exact>
                <PublicRoutes component={<ClinicDetails />} />
              </Route>
              <Route path={SUBMIT_FEEDBACK} exact>
                <PublicRoutes component={<SubmitFeedback />} />
              </Route>
              <Route path={DEFAULT}>
                <RedirectRoute />
              </Route>
            </Switch>
          </LocationWrapper>
          {isLoading ? null : <Footer />}
          </React.Fragment>
        </Suspense>
      </Router>
    </DentalInsuranceProvider>
  );
};

export default App;
